import React, { ReactNode, useEffect, useRef } from "react";
import { theme } from "../config/theme";
import { MultiSelect as MS } from "react-multi-select-component";
// import { Option as Opt } from "react-multi-select-component/dist/index.d.ts";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { Item } from "src/models";
import { format } from "date-fns";
import { Icon } from "./icon";
import { InputGroup, InputLabel } from ".";
import { IItem } from "src/types";

export interface Option<T = any> {
    value:     T;
    label:     string;
    key?:      string;
    disabled?: boolean;
}

type LabelVariant = "body" | "white";
export interface IMultiSelectProps {
  
  disabled?:     boolean,
  expanded?:     boolean,
  forceSelect?:  boolean,       // force at least one selection
  isLoading?:    boolean,
  label?:        string,
  labelVariant?: LabelVariant,
  options:       Option[],
  placeholder?:  string,
  selected:      Option[],
  className?:    string,
  showLoadMore?: boolean,
  singleSelect?: boolean,
  closeOnSelect?: boolean,
  disableSearch?: boolean,
  itemRenderer?  (props: ItemRendererProps): React.ReactNode,
  onAdd?        (selection: Option):         void;             // Called in a multiselect when an item was added
  onChange?     (selection: Option | null):  void;             // Called when single Select is active
  onLoad?       ():                          void,
  onRemove?     (selection: Option):         void;             // Called in a multiselect when an item was removed
  valueRenderer?(items: Option[]):           ReactNode,
  onOpenSelect? (open: boolean):             void,


  disableAutoFocus?:  boolean, // TODO Hacky! need to patch the package instead in select-panel/index.tsx set focusIdx state default to null and check in update function if its != null before calling focus
}

/**
 * Generic multi select component, styled to follow the OP theme
 * @param className
 * @param options       Option[]  Options to choose from
 * @param selected      Option[]  Pre-selected values
 * @param label         ?string   Label to be placed above the input
 * @param singleSelect  ?boolean  User can only select one item
 * @param forceSelect   ?boolean  Force at least one selection
 * @param disabled      ?boolean  Disable input
 * @param disableSearch ?boolean  Disable Search
 * @param isLoading     ?boolean  Show placeholder while options are loading
 * @param placeholder   ?string   String shown in empty input field 
 * @param showLoadMore  ?boolean  Show a custom option at the bottom of the list that can be user to trigger additional data loads.
 * @param placeholder   ?string   String shown in empty input field
 * @param onChange      ?(selection: Option | null): void Called when single Select is active
 * @param onAdd         ?(selection: Option): void        Called in a multiselect when an item was added
 * @param onRemove      ?(selection: Option): void        Called in a multiselect when an item was removed
 * @param onLoad        ?(): void                         Called when the button displayed when @showLoadMore is true is clicked. 
 * @param onOpenSelect  ?(): void                         Called when the select menu is opened
 */

export const MultiSelect = (props: IMultiSelectProps): JSX.Element => {
  const {
    disabled,
    forceSelect,
    onAdd,
    className,
    onChange,
    labelVariant,
    isLoading,
    onLoad,
    showLoadMore,
    onRemove,
    options,
    placeholder,
    selected,
    singleSelect,
    disableSearch = false,
    closeOnSelect = false,
    label,
    onOpenSelect,
    disableAutoFocus = true
  } = props;

  const { t, i18n } = useTranslation();

  const ref = useRef(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const loadMoreOptionKey = "#load_more";
  const loadMoreOption: Option = {
    key:   loadMoreOptionKey,
    label: "",
    value: (
      <LoadingButtonContainer>
        <Icon iconId="chevronDown" size="small" color={theme.color.grayLight} />
      </LoadingButtonContainer>
    ),
  }

  const string_overwrite = {
    allItemsAreSelected: t("all_items_are_selected"),         // "All items are selected"
    clearSearch:         t("clear_search"),                   // "Clear Search"
    noItemSelected:      "",
    noOptions:           t("no_options"),                     // "No options"
    search:              t("search"),                         // "Search",
    selectAll:           t("select_all"),                     // "Select All,"
    selectSomeItems:     placeholder || "",
  };

  const itemRenderer = ({
    checked,
    option,
    disabled,
    onClick
  }: ItemRendererProps): React.ReactNode => {
    return (
      <div 
        className = {`item-renderer ${disabled && "disabled"}`}
      >
        <input
          type     = "checkbox"
          onChange = {(option.key === loadMoreOptionKey ? onLoad : onClick) || (() => {}) }
          checked  = {checked}
          tabIndex = {-1}
          disabled = {disabled}
          style    = {{ height: 0, width: 0, visibility: 'hidden', position: 'absolute' }}
        />
        <span>
          {option.key === loadMoreOptionKey ? option.value : option.label}
        </span>
      </div>
    );
  }

  const valueRenderer = (items: Option[]) =>
    items
      .filter((i) => Boolean(i))
      .map((i) => i.label)
      .join(", ");

  const values = React.useMemo(() => (
    selected && Array.isArray(selected) ? 
    selected.filter(o => Boolean(o) && Boolean(o.label) && Boolean(o.value))
    : []
  ), [selected]);

  const filterOptions = (options: Option[], filter: string) => {
    if (!filter) {
      return options;
    }
    return options.filter(({ value }: {value: Item<IItem>}) => {
      const searchString = [value.title, value.number, format(value.date || new Date(), "yyyy-MM-dd")].join(" ");
      return searchString.includes(filter);
    });
  };

  useEffect(() => {
    if (!ref.current) return;

    const handleClick = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
      setIsOpen(val => !val);
      e.stopPropagation();
    };

    // This is not the ideal way to do this, but we can't access the library's heading from React.
    // TODO: Also handle keyboard events.
    const headingEl = ref.current.querySelector('.dropdown-heading');

    headingEl.addEventListener('click', handleClick);

    return () => {
      headingEl.removeEventListener('click', handleClick);
    }
  }, [ref])

  useEffect(() => {
    // if (!isOpen || !ref) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [ref]);

  const onMenuToggle = React.useCallback((open) => {
    
    if(disableAutoFocus && selected.length) ref?.current?.querySelector(`label[tabindex='${options.findIndex(o => {
      return o.label === selected[0].label
    })}']`)?.focus();

    if(disableAutoFocus && !selected.length) {
      ref?.current?.querySelector(`label[tabindex='0']`)?.blur();
    }

    onOpenSelect && onOpenSelect(open);
  }, [ref]);

  return (
    <InputGroup className={className} ref={ref}>
      <StyledMultiSelect 
        
        ArrowRenderer     = {ArrowRenderer}
        ClearSelectedIcon = {<></>}
        ItemRenderer      = {props.itemRenderer  || itemRenderer}
        disabled          = {disabled}
        className         = {className}
        filterOptions     = {filterOptions}
        hasSelectAll      = {false}
        isLoading         = {isLoading}
        labelledBy        = {label}
        options           = {(options  || []).concat(showLoadMore ? loadMoreOption : [])}
        // options           = {(options  || [])}
        overrideStrings   = {string_overwrite}
        value             = {selected}
        valueRenderer     = {props.valueRenderer || valueRenderer}
        disableSearch     = {disableSearch || (options  || []).length < 4}
        isOpen            = {isOpen}
        onMenuToggle      = {onMenuToggle}
        onChange          = {(selection: Option[]) => {
          // Maybe closeOnSelect should only apply if singleSelect is also true?
          // if (closeOnSelect) setIsOpen(false);
          // Check if the selection is empty
          /* if (closeOnSelect) setIsOpen(false); */
          if(forceSelect && !selection?.length) return;

          // Single select:
          if(singleSelect) {
            const newSelection = selection.length ? selection[selection.length-1] : null;
            onChange(newSelection);

            if (closeOnSelect) {
              setIsOpen(false);
            }
          } else { // Multi select
            // Item added
            if(selection.length > selected.length) {
              onAdd(selection[selection.length-1]);
            } else{ // Item removed
                for(let i = 0; i < selection.length; i++) {
                  if(selected[i].value !== selection[i].value) {
                    onRemove(selected[i]);
                    return;
                  }
                }
                onRemove(selected[Math.max(selected.length - 1, 0)]);
              }
            }
          }
        }
      />
      <StyledLabel labelVariant={labelVariant} focused={Boolean(selected.length)}>{label}</StyledLabel>
    </InputGroup>  
  );
};

export interface ItemRendererProps {
  checked: boolean;
  option: Option;
  className: string;
  disabled?: boolean;
  onClick(): void;
}

const IconWrapper = styled.div<{ $expanded: boolean }>`
  transition: transform 0.3s ease;

  ${p => p.$expanded && css`
    transform: rotate(180deg); 
  `}
`;

const ArrowRenderer = ({ expanded }: { expanded: boolean })  => (
  <IconWrapper $expanded={expanded}>
    <Icon iconId="chevronDown" size="default" color={"black"}/>
  </IconWrapper>
);

const StyledLabel = styled(InputLabel)<{labelVariant: LabelVariant;}>`
  background: ${(props) => props.labelVariant === "body" ? theme.color.grayLightest : theme.color.white };
  text-transform: capitalize;
`;

const StyledMultiSelect = styled(MS)`
  min-width: 16rem;
  
  && {
    --rmsc-main: 1px solid ${theme.color.black};
    --rmsc-hover: ${theme.color.input.hover};
    --rmsc-selected: ${theme.color.input.select};
    --rmsc-border: ${theme.color.input.border};
    --rmsc-gray: transparent; /* Set this to transparent to remove placeholder from under the label */
    --rmsc-bg: transparent;
    --rmsc-p: 1.8rem; /* Spacing */
    --rmsc-radius: 0.4rem; /* Radius */
    --rmsc-h: 4rem; /* Height */
    font-size: 1.4rem;
  }

  .gray {
    margin-right: 2rem;
  }

  .dropdown-container .dropdown-heading {
    height: fit-content;
    min-height: var(--rmsc-h);
  }

  .dropdown-container  .dropdown-content {
    z-index: 3 !important;
  }

  .dropdown-container .dropdown-heading-value {
    font-size: 1.6rem;
    margin: 0.875rem 0rem;
    span {
      text-transform: capitalize;
    }
  }

  .search {
    background: white;
  }

  .select-item * {
    transition: none;
    outline: none;
  }

  .options li {
    list-style: none;
    margin: 0;
    background: ${theme.color.white};
  }
`;


const LoadingButtonContainer = styled.div`
  display:         flex;
  justify-content: center;
  align-items:     center;
`;
